import {
  AccountInfo,
  AuthenticationResult,
  BrowserAuthError,
  InteractionRequiredAuthError,
  IPublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';

import { getSiteSettings } from '../../../site-settings/api';
import { SiteSettingsScope } from '../../../site-settings/enums';
import { AuthorizationSettings } from '../../../site-settings/models';
import { authenticate, buildB2cPolicies, getMsalClient, signOut } from '../../uip';
import { getAccount } from '../../uip/utilities/get-account';
import { initializeWebStoreHttp } from '../api';
import { TOKEN_VALIDATION_INTERVAL } from '../constants/token-validation-interval.const';
import { StorageKey } from '../enums';
import { getAuthToken, getGuestToken, validateJWTToken } from '../utilities';

import { getWebStoreApiConfig } from '../config';

declare global {
  interface Window {
    WebStoreApiClient?: WebStoreApiClient; // Marked as optional since it may not exist initially
  }
}

class WebStoreApiClient {
  private readonly msalClient: IPublicClientApplication;
  private workerId?: number;

  public onTokenAcquired: Promise<string>;

  constructor() {
    initializeWebStoreHttp(getWebStoreApiConfig());
    this.workerId = this.registerWorker();
    this.msalClient = getMsalClient();

    this.onTokenAcquired = this.msalClient.handleRedirectPromise().then(this.handleResponse);
  }

  public get account(): AccountInfo | undefined {
    return getAccount();
  }

  public get isAuthorized(): boolean {
    return !!this.account;
  }

  public async signIn() {
    const { marketLocale, scopes } = getWebStoreApiConfig();
    const signInRequest: RedirectRequest = {
      extraQueryParameters: { ui_locales: marketLocale },
      scopes,
    };

    try {
      await authenticate(signInRequest);
    } catch (error) {
      if (error instanceof BrowserAuthError && error.errorCode === 'interaction_in_progress') {
        // Clear the stuck state and retry authentication
        await this.clearAuthenticationState();
        await authenticate(signInRequest);
      } else {
        throw error;
      }
    }
  }

  public async signOut() {
    if (!this.account) return;

    this.storage.removeItem(StorageKey.WebStoreAuthToken);
    this.storage.removeItem(StorageKey.CustomerAuthId);
    await signOut(this.account);
  }

  public async signUp() {
    const { marketLocale, scopes } = getWebStoreApiConfig();
    const settings: AuthorizationSettings | null = await getSiteSettings<AuthorizationSettings>(
      marketLocale,
      SiteSettingsScope.Authorization
    );
    if (!settings) return;

    const { authorities } = buildB2cPolicies(settings);
    const signUpRequest: RedirectRequest = {
      authority: authorities.signUp.authority,
      extraQueryParameters: { ui_locales: marketLocale },
      scopes,
    };
    await authenticate(signUpRequest);
  }

  private async clearAuthenticationState() {
    try {
      // Clear any ongoing interactions
      if (typeof window !== 'undefined') {
        window.sessionStorage.clear();
      }
      // Handle any pending redirects to clear the state
      await this.msalClient.handleRedirectPromise();
    } catch (error) {
      console.warn('Error clearing authentication state:', error);
    }
  }

  private async acquireToken(request: SilentRequest): Promise<string> {
    try {
      const { accessToken } = await this.msalClient.acquireTokenSilent(request);
      return accessToken;
    } catch (error) {
      console.warn(error);

      // Error occurred when refresh token is expired (24+ hours)
      if (error instanceof BrowserAuthError) {
        await this.signOut();
      } else if (error instanceof InteractionRequiredAuthError) {
        await this.msalClient.acquireTokenRedirect(request);
      }

      return '';
    }
  }

  private async ensure(): Promise<string> {
    if (!this.isAuthorized) return await getGuestToken();

    const request: SilentRequest = this.getSilentRequest();
    const accessToken: string = await this.acquireToken(request);

    return await getAuthToken(accessToken, await getGuestToken());
  }

  private validate = async (): Promise<string> => {
    const storageKey: StorageKey = this.account
      ? StorageKey.WebStoreAuthToken
      : StorageKey.WebStoreGuestToken;
    const token: string | null = this.storage.getItem(storageKey);
    if (!token) return (this.onTokenAcquired = this.ensure());

    if (!validateJWTToken(token)) {
      this.resetWorker();
      return (this.onTokenAcquired = this.ensure());
    }

    return token;
  };

  private resetWorker() {
    this.cancelWorker();
    this.workerId = this.registerWorker();
  }

  private registerWorker(): number {
    return window.setInterval(this.validate, TOKEN_VALIDATION_INTERVAL);
  }

  private cancelWorker() {
    window.clearInterval(this.workerId);
  }

  private getSilentRequest(account: AccountInfo | undefined = this.account): SilentRequest {
    const { scopes } = getWebStoreApiConfig();
    return {
      account,
      forceRefresh: false,
      scopes,
    };
  }

  private handleResponse = async (response: AuthenticationResult | null): Promise<string> => {
    // Nothing happens, so we should return one of the tokens stored in the storage.
    if (response === null) return await this.ensure();

    const request: SilentRequest = this.getSilentRequest(response.account || undefined);
    const accessToken: string = await this.acquireToken(request);

    return await getAuthToken(accessToken || '', await getGuestToken());
  };

  private get storage(): Storage {
    return getWebStoreApiConfig().storage;
  }
}

/** Singleton */
let webStoreApiClient: WebStoreApiClient;

export const getWebStoreApiClient = (): WebStoreApiClient => {
  // Check whether it's browser environment or not.
  if (typeof window === 'undefined')
    return webStoreApiClient || (webStoreApiClient = new WebStoreApiClient());

  if (!window.WebStoreApiClient)
    window.WebStoreApiClient = webStoreApiClient || (webStoreApiClient = new WebStoreApiClient());

  return window.WebStoreApiClient;
};
